import {
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faArrowCircleLeft, faArrowCircleRight, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe } from "@ngx-translate/core";
import {
  DndDraggableDirective,
  DndDropEvent,
  DndDropzoneDirective,
  DndModule,
  DndPlaceholderRefDirective,
} from "ngx-drag-drop";

import { AdaaHelper } from "../../../../core/utils";
import { Language } from "../../../constants/enums";
import { ParameterCatalog, ValueText } from "../../../models";
import { LanguageService } from "../../../services";

@Component({
  selector: "adaa-form-two-way-select",
  imports: [
    TranslatePipe,
    FontAwesomeModule,
    DndModule,
    DndDropzoneDirective,
    DndDraggableDirective,
    DndPlaceholderRefDirective,
  ],
  templateUrl: "./form-two-way-select.html",
  styleUrl: "./form-two-way-select.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormTwoWaySelectComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormTwoWaySelectComponent),
    },
  ],
})
export class FormTwoWaySelectComponent implements ControlValueAccessor {
  private readonly _iconLibrary = inject(FaIconLibrary);
  readonly languageService = inject(LanguageService);
  readonly AdaaHelper = AdaaHelper;
  readonly Language = Language;

  readonly required = input<boolean>(false);
  readonly label = input<string>("");
  readonly selectedLabel = input<string>("");
  readonly notSelectedLabel = input<string>("");
  readonly options = input<ValueText[]>([]);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly emptyNotSelectedListLabel = input<string>("common.form.label.drag_drop_no_items_to_add");
  readonly emptySelectedListLabel = input<string>("common.form.label.drag_drop_no_items_to_remove");
  readonly isDisabled = input<boolean>(false);
  readonly allowSort = input<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly setDefaultValue = input<any>(null);
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] } | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly inputControl = new FormControl();
  readonly notSelectedList = signal<ValueText[]>([]);
  readonly selectedList = signal<ValueText[]>([]);
  readonly notSelectedListCopy = signal<ValueText[]>([]);
  readonly selectedListCopy = signal<ValueText[]>([]);
  readonly searchResults = signal<boolean>(false);

  private readonly _searchNotSelected = viewChild<ElementRef>("searchNotSelected");
  private readonly _searchSelected = viewChild<ElementRef>("searchSelected");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly selectedHighlight = signal<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly notSelectedHighlight = signal<any[]>([]);

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _disabledFromControl = signal<boolean>(false);

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());
  readonly isControlDisabled = computed(() => this.isDisabled() || this._disabledFromControl());

  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onChange: (value: any) => void;

  constructor() {
    this._iconLibrary.addIcons(faArrowLeft, faArrowRight, faArrowCircleRight, faArrowCircleLeft);

    effect(() => {
      const opts = this.options();
      untracked(() => {
        this.notSelectedList.set(opts ?? []);
        this.selectedList.set([]);
        this.writeValue(this.inputControl.value);
      });
    });

    effect(() => {
      const v = this.setDefaultValue();
      if (v) untracked(() => this.writeValue(v));
    });

    effect(() => {
      const validatorList = this.setValidator();
      if (validatorList?.parameterCatalogs) {
        const controlName = untracked(() => this.controlName());
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === controlName);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === "Y");
            this.inputControl.updateValueAndValidity();
          });
        }
      }
    });
  }

  public search(event: string, isAvailable: boolean = false): void {
    const lowerCaseSearch = event.toLowerCase();

    if (isAvailable) {
      if (!this.notSelectedListCopy().length) this.notSelectedListCopy.set(this.notSelectedList());
      const targetList = this.notSelectedListCopy().length ? this.notSelectedListCopy() : this.options();

      this.notSelectedList.set(
        targetList?.filter((a) => AdaaHelper.asText(a.text).toLowerCase().includes(lowerCaseSearch)) ?? []
      );
      this.searchResults.set(this.notSelectedList().length > 0);

      if (event.length === 0) this.notSelectedList.set(this.notSelectedListCopy());
    } else {
      if (!this.selectedListCopy().length) this.selectedListCopy.set(this.selectedList());
      const targetList = this.selectedListCopy().length ? this.selectedListCopy() : this.options();

      this.selectedList.set(
        targetList?.filter((a) => AdaaHelper.asText(a.text).toLowerCase().includes(lowerCaseSearch)) ?? []
      );
      this.searchResults.set(this.selectedList().length > 0);

      if (event.length === 0) this.selectedList.set(this.selectedListCopy());
    }
    if (event.length === 0) this.searchResults.set(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectItem(event: any, isAvailable: boolean = false): void {
    if (this.isControlDisabled()) return;

    const highlight = isAvailable ? this.selectedHighlight : this.notSelectedHighlight;

    highlight.update((items) => {
      const index = items.findIndex((e) => e === event);
      if (index === -1) return [...items, event];

      const next = [...items];
      next.splice(index, 1);
      return next;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public hasItem(event: any, isAvailable: boolean = false): boolean {
    if (isAvailable) {
      return this.selectedHighlight().findIndex((e) => e === event) !== -1;
    } else {
      return this.notSelectedHighlight().findIndex((e) => e === event) !== -1;
    }
  }

  public selectAll(isAvailable: boolean = false): void {
    if (this.isControlDisabled()) return;

    if (this.searchResults()) {
      if (isAvailable) {
        const selected = [...this.selectedList()];
        this.notSelectedList().forEach((result) => {
          if (!selected.find((option) => option.value === result.value)) {
            selected.push(result);
          }
        });
        const notSelected = this.options().filter((e) => !selected.includes(e));

        this.selectedList.set(selected);
        this.selectedListCopy.set(selected);
        this.notSelectedList.set(notSelected);
        this.notSelectedListCopy.set(notSelected);
      } else {
        const notSelected = [...this.notSelectedList()];
        this.selectedList().forEach((result) => {
          if (!notSelected.find((option) => option.value === result.value)) notSelected.push(result);
        });
        const selected = this.options().filter((e) => !notSelected.includes(e));

        this.notSelectedList.set(notSelected);
        this.selectedList.set(selected);
        this.selectedListCopy.set(selected);
      }
    } else {
      if (isAvailable) {
        const selected = AdaaHelper.clone(this.options());

        this.selectedList.set(selected);
        this.selectedListCopy.set(selected);
        this.notSelectedList.set([]);
      } else {
        this.notSelectedList.set(this.options() ?? []);
        this.selectedList.set([]);
        this.selectedListCopy.set([]);
      }
    }

    this.searchResults.set(false);
    this._setInputValue();
    this.selectedHighlight.set([]);
    this.notSelectedHighlight.set([]);

    this.doSearch();
  }

  public select(isAvailable: boolean = false): void {
    if (this.isControlDisabled()) return;

    if (isAvailable) {
      const highlights = this.selectedHighlight();
      if (!AdaaHelper.isDefined(highlights) || highlights.length === 0) return;

      const selected = [...this.selectedListCopy()];
      highlights.forEach((highlight) => {
        const option = this.options().find((option) => option.value === highlight);
        if (option) {
          if (!selected.find((option) => option.value === highlight)) selected.push(option);
        }
      });
      this.selectedList.set(selected);
      this.selectedListCopy.set(selected);

      const selectedValues = selected.map((e) => e.value);
      const notSelected = this.options().filter((e) => !selectedValues.includes(e.value));
      this.notSelectedList.set(notSelected);
      this.notSelectedListCopy.set(AdaaHelper.clone(notSelected));
    } else {
      const highlights = this.notSelectedHighlight();
      if (!AdaaHelper.isDefined(highlights) || highlights.length === 0) return;

      const notSelected = [...this.notSelectedListCopy()];
      highlights.forEach((highlight) => {
        const option = this.options().find((option) => option.value === highlight);
        if (option) {
          if (!notSelected.find((option) => option.value === highlight)) notSelected.push(option);
        }
      });
      this.notSelectedList.set(notSelected);
      this.notSelectedListCopy.set(notSelected);

      const notSelectedValues = notSelected.map((e) => e.value);
      const selected = this.options().filter((e) => !notSelectedValues.includes(e.value));
      this.selectedList.set(selected);
      this.selectedListCopy.set(AdaaHelper.clone(selected));
    }
    this._setInputValue();
    this.selectedHighlight.set([]);
    this.notSelectedHighlight.set([]);

    this.doSearch();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public writeValue(obj: any): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    if (AdaaHelper.isDefined(obj)) {
      this.selectedList.set(
        this.options()?.filter((option) => obj.some((item: { value: unknown }) => item === option.value)) ?? []
      );
      this.notSelectedList.set(
        this.options()?.filter((option) => !obj.some((item: { value: unknown }) => item === option.value)) ?? []
      );
    }

    this.doSearch();
    this.inputControl.setValue(obj);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this._disabledFromControl.set(isDisabled);

    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isEmpty = this.inputControl.value === null || this.inputControl.value === "";
    return this.isRequired() && isEmpty && { required: true };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public _valueChanged(event: any): void {
    if (this._onChange) this._onChange(event);

    this.inputChanges.emit(event);
  }

  public doSearch() {
    if (!this._searchNotSelected() || !this._searchSelected()) return;

    this.notSelectedListCopy.set([]);
    this.selectedListCopy.set([]);

    this.search(this._searchNotSelected()!.nativeElement.value, true);
    this.search(this._searchSelected()!.nativeElement.value);
  }

  public onDrop(event: DndDropEvent, isSelected?: boolean): void {
    if (this.isControlDisabled()) return;
    if (event.index == undefined) return;
    if (isSelected) {
      if (!this.selectedList().find((e) => e.value === event.data.value)) {
        const selected = [...this.selectedList()];
        selected.splice(event.index, 0, event.data);
        const selectedValues = selected.map((e) => e.value);
        const notSelected = this.options().filter((e) => !selectedValues.includes(e.value));

        this.selectedList.set(selected);
        this.selectedListCopy.set(selected);
        this.notSelectedList.set(notSelected);
        this.notSelectedListCopy.set(notSelected);
      } else {
        const index = this.selectedList().findIndex((e) => e.value === event.data.value);
        if (index != -1) {
          const selected = AdaaHelper.moveItemInArray([...this.selectedList()], index, event.index);

          this.selectedList.set(selected);
          this.selectedListCopy.set(selected);
        }
      }
    } else {
      if (!this.notSelectedList().find((e) => e.value === event.data.value)) {
        const notSelected = [...this.notSelectedList()];
        notSelected.splice(event.index, 0, event.data);
        const notSelectedValues = notSelected.map((e) => e.value);
        const selected = this.options().filter((e) => !notSelectedValues.includes(e.value));

        this.notSelectedList.set(notSelected);
        this.notSelectedListCopy.set(notSelected);
        this.selectedList.set(selected);
        this.selectedListCopy.set(selected);
      } else {
        const index = this.notSelectedList().findIndex((e) => e.value === event.data.value);
        if (index != -1) {
          const notSelected = AdaaHelper.moveItemInArray([...this.notSelectedList()], index, event.index);

          this.notSelectedList.set(notSelected);
          this.notSelectedListCopy.set(notSelected);
        }
      }
    }

    this._setInputValue();
  }

  private _setInputValue() {
    const ids = this.selectedList().map((e) => e.value);
    this.inputControl.setValue(ids);
    this._valueChanged(ids);
  }
}
